/**
 * Follow-Up New Issues Page
 *
 * Asks existing patients about any new issues since their last visit.
 * - If "No new issues" checkbox is checked → Skip to Review
 * - If text is entered → Continue to Module Selection
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFollowUp } from '../context/FollowUpContext';
import { useTenant } from '../context/TenantContext';
import defaultLogo from '../assets/logo.png';
import logger from '../utils/logger';
import activityLogger from '../utils/activityLogger';
import '../styles/formPage.css';
import '../styles/followup.css';

const NEW_ISSUES_STEP = [3, 'New Issues'];

const FollowUpNewIssues = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const {
    isExistingUser,
    isNewUser,
    updateFields,
    submissionInfo,
    previousData,
  } = useFollowUp();

  const logoToDisplay = tenant?.logoUrl || defaultLogo;

  const [noNewIssues, setNoNewIssues] = useState(false);
  const [newIssuesText, setNewIssuesText] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  // Give session storage a moment to be read (runs once on mount)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSessionReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    activityLogger.stepStarted(...NEW_ISSUES_STEP);
  }, []);

  // Redirect if not existing user (only after session is ready)
  useEffect(() => {
    if (sessionReady && !isExistingUser) {
      logger.log('[NewIssues] Not an existing user, redirecting to /');
      navigate('/');
    }
  }, [isExistingUser, sessionReady, navigate]);

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setNoNewIssues(checked);
    setError('');

    // If checkbox is checked, clear the textarea
    if (checked) {
      setNewIssuesText('');
    }
  };

  // Handle textarea change
  const handleTextChange = (e) => {
    const text = e.target.value;
    setNewIssuesText(text);
    setError('');

    // If text is entered, uncheck the checkbox
    if (text.trim()) {
      setNoNewIssues(false);
    }
  };

  // Handle form submission
  const handleNext = async () => {
    setError('');

    // Validation: Either checkbox must be checked OR textarea must have content
    if (!noNewIssues && !newIssuesText.trim()) {
      setError('Please either describe your new issues or check the box if you have none.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Save the new issues data
      const newIssuesData = {
        hasNewIssues: !noNewIssues,
        newIssuesDescription: noNewIssues ? 'No new issues since last visit' : newIssuesText.trim(),
        newIssuesTimestamp: new Date().toISOString(),
      };

      // Update fields in context
      updateFields({
        newissues: newIssuesData.newIssuesDescription,
        hasnewissues: newIssuesData.hasNewIssues ? 'Yes' : 'No',
        newissuestimestamp: newIssuesData.newIssuesTimestamp,
      });

      activityLogger.stepCompleted(...NEW_ISSUES_STEP);

      if (noNewIssues) {
        // No new issues - skip module selection, go directly to review
        navigate('/review');
      } else {
        // Has new issues - go to module selection
        navigate('/review');
      }
    } catch (err) {
      logger.error('Error saving new issues:', err?.message);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle back
  const handleBack = () => {
    navigate('/');
  };

  // Show loading while session is being restored
  if (!sessionReady) {
    return (
      <div>
        <div className="logo-img d-flex-center">
          <div style={{ textAlign: 'center' }}>
            <img src={logoToDisplay} alt={tenant?.brandName || 'Logo'} style={{ width: '200px', height: 'auto', objectFit: 'contain', marginTop: '10px' }} />
          </div>
        </div>
        <div className="form-container" style={{ maxWidth: '700px', margin: '20px auto', padding: '30px' }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="followup-spinner"></div>
            <p style={{ color: '#6c757d', fontSize: '14px' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isExistingUser) return null;

  // Get patient name for greeting
  const patientName = submissionInfo?.patientInfo?.firstName
    || previousData?.firstname
    || previousData?.firstName
    || 'Patient';

  return (
    <div>
      {/* Logo */}
      <div className="logo-img d-flex-center">
        <div style={{ textAlign: 'center' }}>
          <img
            src={logoToDisplay}
            alt={tenant?.brandName || 'Logo'}
            style={{ width: '200px', height: 'auto', objectFit: 'contain', marginTop: '10px' }}
          />
        </div>
      </div>

      {/* Form Container */}
      <div className="form-container" style={{ maxWidth: '700px', margin: '20px auto', padding: '30px' }}>

        {/* Welcome Message */}
        <div style={{
          textAlign: 'center',
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: 'var(--color-primary-light, #e8f5f1)',
          borderRadius: '8px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: 'var(--color-primary, #06AB8B)',
            marginBottom: '10px'
          }}>
            Welcome Back, {patientName}!
          </h2>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
            Before we continue, please let us know if anything has changed since your last visit.
          </p>
        </div>

        {/* Section Title */}
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#333',
          marginBottom: '20px',
          paddingBottom: '10px',
          borderBottom: '2px solid var(--color-primary, #06AB8B)'
        }}>
          Update since your last visit
        </h3>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#c33',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Textarea for new issues */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: '500',
            color: '#333',
            fontSize: '14px'
          }}>
            Please describe any new issues, symptoms, hospitalizations, or changes since your last appointment:
          </label>
          <textarea
            value={newIssuesText}
            onChange={handleTextChange}
            placeholder="Enter response here"
            disabled={noNewIssues}
            style={{
              width: '100%',
              minHeight: '150px',
              padding: '12px 15px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              resize: 'vertical',
              fontFamily: 'inherit',
              backgroundColor: noNewIssues ? '#f5f5f5' : '#fff',
              color: noNewIssues ? '#999' : '#333',
              transition: 'all 0.2s ease'
            }}
          />
        </div>

        {/* Divider */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          margin: '25px 0',
        }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
          <span style={{ padding: '0 15px', fontSize: '13px', color: '#000', fontWeight: '500' }}>Or</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
        </div>

        {/* No new issues - immediate action, navigate to review (read-only, no update buttons) */}
        <button
          onClick={() => {
            setNoNewIssues(true);
            activityLogger.stepCompleted(...NEW_ISSUES_STEP);
            updateFields({ hasNewIssues: false, newIssuesDescription: 'No new issues since last visit', hasnewissues: 'No', newissues: '' });
            navigate('/review', { state: { noNewIssues: true } });
          }}
          style={{
            width: '100%',
            padding: '14px 20px',
            marginBottom: '30px',
            backgroundColor: noNewIssues ? 'var(--color-primary, #4a7a2c)' : '#fff',
            color: noNewIssues ? '#fff' : '#333',
            border: noNewIssues ? '2px solid var(--color-primary, #4a7a2c)' : '1px solid #c2cad3',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            textAlign: 'center',
          }}
        >
          {noNewIssues ? '✓ ' : ''}No new issues, proceed to review your information
        </button>

        {/* Navigation Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
          paddingTop: '20px',
          borderTop: '1px solid #eee'
        }}>
          <button
            type="button"
            onClick={handleBack}
            disabled={isSubmitting}
            style={{
              padding: '12px 30px',
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #6c757d',
              background: '#fff',
              color: '#6c757d',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={isSubmitting}
            style={{
              padding: '12px 30px',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              background: 'var(--color-primary, #06AB8B)',
              color: '#fff',
              borderRadius: '6px',
              cursor: 'pointer',
              minWidth: '150px',
              transition: 'all 0.2s ease'
            }}
          >
            {isSubmitting ? 'Processing...' : (noNewIssues ? 'Continue to Review' : 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FollowUpNewIssues;
